export type FunnelbackFetchInit = RequestInit & { signal?: AbortSignal };

export type FunnelbackFetchRetryOptions = {
  /** Total attempts including the first (default 3). */
  maxAttempts?: number;
  /** Delay before retry attempt 2, in ms (default 300). */
  initialDelayMs?: number;
  /** Multiplier applied to delay after each retry (default 3). */
  backoffMultiplier?: number;
};

const DEFAULT_MAX_ATTEMPTS = 3;
const DEFAULT_INITIAL_DELAY_MS = 300;
const DEFAULT_BACKOFF_MULTIPLIER = 3;

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  if (ms <= 0) {
    return Promise.resolve();
  }
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(signal.reason ?? new DOMException('Aborted', 'AbortError'));
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      reject(signal!.reason ?? new DOMException('Aborted', 'AbortError'));
    };
    const timer = window.setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/** Network failures from cross-origin Funnelback fetches (browser-specific messages). */
export function isFunnelbackNetworkFetchError(err: unknown): boolean {
  if (!(err instanceof TypeError)) {
    return false;
  }
  const message = err.message;
  return (
    message.includes('Failed to fetch') ||
    message.includes('Load failed') ||
    message.includes('NetworkError')
  );
}

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException
    ? err.name === 'AbortError'
    : err instanceof Error && err.name === 'AbortError';
}

/**
 * `fetch` with short backoff retries for transient browser network failures
 * (e.g. corporate proxy blips on page-load Funnelback requests).
 * Does not retry HTTP error responses or aborted requests.
 */
export async function fetchWithFunnelbackRetry(
  url: string,
  init: FunnelbackFetchInit = {},
  options: FunnelbackFetchRetryOptions = {},
): Promise<Response> {
  const maxAttempts = Math.max(1, options.maxAttempts ?? DEFAULT_MAX_ATTEMPTS);
  const initialDelayMs = options.initialDelayMs ?? DEFAULT_INITIAL_DELAY_MS;
  const backoffMultiplier =
    options.backoffMultiplier ?? DEFAULT_BACKOFF_MULTIPLIER;

  let lastError: unknown;
  let delayMs = initialDelayMs;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    if (init.signal?.aborted) {
      throw init.signal.reason ?? new DOMException('Aborted', 'AbortError');
    }

    try {
      return await fetch(url, init);
    } catch (err) {
      if (isAbortError(err)) {
        throw err;
      }
      lastError = err;
      if (!isFunnelbackNetworkFetchError(err) || attempt >= maxAttempts) {
        throw err;
      }
      await sleep(delayMs, init.signal);
      delayMs *= backoffMultiplier;
    }
  }

  throw lastError;
}
